import React, { useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import useShippingRequest, { shippingAccessScope } from '../../hooks/useShippingRequest';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import { useLanguage } from '../../i18n/LanguageContext';

export const ACCOUNTING_STATUS_LABELS = {
  ready: 'Queued',
  invoice_unknown: 'Invoice needs verification',
  invoice_recorded: 'Invoice recorded',
  payment_unknown: 'Payment needs verification',
  recorded: 'Recorded',
  needs_review: 'Needs review',
};
const MONITOR_LABELS = {
  off: 'Off',
  unverified: 'No successful monitor run verified',
  stalled: 'Monitor stalled',
  failed: 'Monitor failed',
  attention: 'Needs attention',
  healthy: 'Monitor healthy',
};
const MONITOR_DATES = {
  lastRunAt: 'Last monitor run',
  lastSuccessAt: 'Last successful accounting check',
  lastErrorAt: 'Last monitor error',
};
const identifier = (value) => typeof value === 'string' && /^[a-f\d]{24}$/.test(value);
const count = (value) => Number.isSafeInteger(value) && value >= 0;
const timestamp = (value) =>
  typeof value === 'string' &&
  /^\d{4}-\d{2}-\d{2}T/.test(value) &&
  Number.isFinite(Date.parse(value));
// Older servers omit health. Invalid or missing evidence must never imply a healthy worker.
export function readAccountingMonitor(value) {
  if (
    typeof value?.enabled !== 'boolean' ||
    value.scope !== 'global' ||
    value.financialReadinessVerified !== false ||
    !Object.hasOwn(MONITOR_LABELS, value.status) ||
    value.intervalMinutes !== 1 ||
    !count(value.pendingCount) ||
    !count(value.manualReviewCount) ||
    !Object.keys(MONITOR_DATES).every((key) => value[key] === null || timestamp(value[key])) ||
    ![null, 'MONITOR_UNVERIFIED'].includes(value.errorCode) ||
    (value.indexReady !== undefined && typeof value.indexReady !== 'boolean') ||
    (value.status === 'off') !== !value.enabled ||
    (['healthy', 'attention', 'stalled'].includes(value.status) &&
      (!timestamp(value.lastSuccessAt) || value.indexReady !== true || value.errorCode !== null)) ||
    (value.status === 'healthy' && (value.pendingCount !== 0 || value.manualReviewCount !== 0)) ||
    (value.status === 'attention' && value.pendingCount + value.manualReviewCount === 0)
  )
    return null;
  return value;
}
export function readAccountingStatus(response, distributorId, page) {
  const value = response?.data?.data;
  if (
    value?.distributorId !== distributorId ||
    value.page !== page ||
    value.limit !== 10 ||
    !count(value.total) ||
    !Array.isArray(value.items) ||
    value.items.length > 10 ||
    !count(value.counts?.pending) ||
    !count(value.counts?.recorded) ||
    !count(value.counts?.needsReview) ||
    value.items.length > value.total ||
    value.counts.pending + value.counts.recorded + value.counts.needsReview !== value.total ||
    value.items.some(
      (item) =>
        !identifier(item?.purchaseId) ||
        !identifier(item.bankEntryId) ||
        !Object.hasOwn(ACCOUNTING_STATUS_LABELS, item.status) ||
        !timestamp(item.createdAt) ||
        !timestamp(item.updatedAt)
    ) ||
    new Set(value.items.map((item) => item.purchaseId)).size !== value.items.length
  )
    throw new Error('Invalid accounting status');
  return { ...value, monitor: readAccountingMonitor(value.monitor) };
}

export default function DistributorAccountingStatus({ distributorId }) {
  const { t, locale } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const pending = useRef(false);
  const session = useRef(captureSession());
  const scope = useRef(shippingAccessScope());
  const { begin } = useShippingRequest(distributorId);
  const current = () =>
    isSessionCurrent(session.current) && scope.current === shippingAccessScope();
  const load = async (requestedPage) => {
    if (pending.current || !current()) return;
    const request = begin();
    pending.current = true;
    setBusy(true);
    setExpanded(true);
    setError(false);
    setReceipt(null);
    try {
      const response = await distributorService.adminAccountingStatus(
        distributorId,
        requestedPage,
        { signal: request.signal }
      );
      if (!request.isCurrent() || !current()) return;
      setReceipt(readAccountingStatus(response, distributorId, requestedPage));
      setPage(requestedPage);
    } catch {
      if (request.isCurrent() && current()) setError(true);
    } finally {
      pending.current = false;
      if (request.isCurrent() && current()) setBusy(false);
    }
  };
  return (
    <section aria-label={t('Accounting posting status')} className="space-y-3 border-t pt-4">
      <h3 className="font-semibold">{t('Accounting posting status')}</h3>
      <p>
        {t(
          'Review existing accounting records. This view does not retry or create financial entries.'
        )}
      </p>
      <button
        type="button"
        className="me-distributor-admin-secondary"
        disabled={busy}
        onClick={() => load(page)}
      >
        {t(expanded ? 'Refresh accounting status' : 'View accounting status')}
      </button>
      {busy && <p role="status">{t('Loading accounting status...')}</p>}
      {error && <p role="alert">{t('Accounting status could not load. Please refresh.')}</p>}
      {receipt && current() && receipt.distributorId === distributorId && (
        <>
          <div className="space-y-2 rounded border p-3" aria-label={t('Accounting monitor health')}>
            <h4 className="font-semibold">{t('Accounting monitor health')}</h4>
            <p>
              {t(
                'Global worker health across all distributors. It does not verify payment or accounting readiness.'
              )}
            </p>
            {receipt.monitor ? (
              <>
                <p role="status">{t(MONITOR_LABELS[receipt.monitor.status])}</p>
                <p>
                  {t('Global pending records: {pending} · Global reviews required: {review}', {
                    pending: receipt.monitor.pendingCount,
                    review: receipt.monitor.manualReviewCount,
                  })}
                </p>
                {Object.entries(MONITOR_DATES).map(([key, label]) => (
                  <p key={key}>
                    {t(label)}:{' '}
                    {receipt.monitor[key]
                      ? new Date(receipt.monitor[key]).toLocaleString(locale)
                      : t('Unavailable')}
                  </p>
                ))}
              </>
            ) : (
              <p role="status">
                {t('Accounting monitor health is unavailable. Readiness is unconfirmed.')}
              </p>
            )}
          </div>
          <p role="status">
            {t('Pending: {pending} · Recorded: {recorded} · Needs review: {review}', {
              pending: receipt.counts.pending,
              recorded: receipt.counts.recorded,
              review: receipt.counts.needsReview,
            })}
          </p>
          {receipt.items.length === 0 ? (
            <p>{t('No accounting records on this page.')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <caption className="sr-only">{t('Accounting posting status')}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t('Purchase reference')}</th>
                    <th scope="col">{t('Bank entry reference')}</th>
                    <th scope="col">{t('Status')}</th>
                    <th scope="col">{t('Updated')}</th>
                  </tr>
                </thead>
                <tbody>
                  {receipt.items.map((item) => (
                    <tr key={item.purchaseId}>
                      <td className="break-all py-2 pr-3">{item.purchaseId}</td>
                      <td className="break-all py-2 pr-3">{item.bankEntryId}</td>
                      <td className="py-2 pr-3">{t(ACCOUNTING_STATUS_LABELS[item.status])}</td>
                      <td className="py-2">
                        <time dateTime={item.updatedAt}>
                          {new Date(item.updatedAt).toLocaleString(locale)}
                        </time>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" disabled={busy || page <= 1} onClick={() => load(page - 1)}>
              {t('Previous accounting page')}
            </button>
            <span>{t('Accounting page {page}', { page })}</span>
            <button
              type="button"
              disabled={busy || page * 10 >= receipt.total}
              onClick={() => load(page + 1)}
            >
              {t('Next accounting page')}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
